import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../css/home/LiveNow.css';
import SectionHeader from './SectionHeader';
import { useLang } from '../../i18n';

const API = process.env.REACT_APP_API_BASE_URL;

function getStatusText(status, t) {
  if (!status) return t('home.status.live');
  const { short, elapsed } = status;
  if (short === 'HT') return t('home.status.halfTime');
  if (['1H', '2H', 'ET'].includes(short) && elapsed) return `${elapsed}'`;
  if (short === 'P') return t('home.status.penalties');
  return t('home.status.live');
}

function mapOdds(match) {
  const mw = match.odds?.match_winner || [];
  return [
    { v: mw.find(o => o.label === 'Home')?.odd ?? '-' },
    { v: mw.find(o => o.label === 'Draw')?.odd ?? '-' },
    { v: mw.find(o => o.label === 'Away')?.odd ?? '-' },
  ];
}

function OddsCell({ value }) {
  const empty = value === '-' || value == null;
  return (
    <div className={'odds-cell' + (empty ? ' is-empty' : '')}>
      <span className="odds-val">{value == null ? '-' : value}</span>
    </div>
  );
}

function LiveCard({ match }) {
  const navigate = useNavigate();
  const { t } = useLang();
  const odds = mapOdds(match);
  const home = match.teams?.home?.name || '';
  const away = match.teams?.away?.name || '';
  const hGoal = match.goals?.home ?? 0;
  const aGoal = match.goals?.away ?? 0;
  const status = getStatusText(match.fixture?.status, t);

  const fmtOdd = (v) => {
    if (v === '-' || v == null) return v;
    const n = parseFloat(v);
    return isNaN(n) ? v : parseFloat(n.toFixed(2));
  };

  const addBet = (e, label, odd, sel) => {
    e.stopPropagation();
    if (!odd || odd === '-') return;
    const bet = {
      id: `live-${match.fixture?.id}-${sel}-${Date.now()}`,
      matchId: match.fixture?.id,
      match: `${home} vs ${away}`,
      date: status,
      selection: sel,
      odds: parseFloat(odd),
      stake: 0,
      league: match.league?.name || 'Football',
      sport: 'football',
      isLive: true,
    };
    const existing = JSON.parse(localStorage.getItem('betSlipItems') || '[]');
    if (!existing.find(b => b.matchId === bet.matchId && b.selection === bet.selection)) {
      localStorage.setItem('betSlipItems', JSON.stringify([...existing, bet]));
      window.dispatchEvent(new Event('betAdded'));
    }
  };

  return (
    <article className="matchcard" onClick={() => navigate(`/match/football/${match.fixture?.id}`)}>
      <div className="matchcard-market">
        <div className="matchcard-teams" style={{ cursor: 'pointer' }}>
          <div className="matchcard-teamrow">
            <span className="matchcard-team">{home}</span>
            <span className="matchcard-score">{hGoal}</span>
          </div>
          <div className="matchcard-teamrow">
            <span className="matchcard-team">{away}</span>
            <span className="matchcard-score">{aGoal}</span>
          </div>
        </div>
        <div className="matchcard-odds">
          {[
            [odds[0].v, '1', 'Match Winner: Home'],
            [odds[1].v, 'X', 'Match Winner: Draw'],
            [odds[2].v, '2', 'Match Winner: Away'],
          ].map(([v, lbl, sel]) => (
            <div key={lbl} className={'odds-cell' + (v === '-' ? ' is-empty' : '')} onClick={(e) => addBet(e, lbl, v, sel)}>
              <span className="odds-val">{fmtOdd(v)}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="matchcard-info">
        <span className="matchcard-live" />
        <span className="matchcard-status">{status}</span>
        {match.league?.name ? <span className="matchcard-extra" style={{ fontSize: 9, opacity: .7 }}>{match.league.name}</span> : null}
      </div>
    </article>
  );
}

export default function LiveNow() {
  const { t } = useLang();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLive = (silent = false) => {
    if (!silent) setLoading(true);
    fetch(`${API}/api-bet/football/live.php`)
      .then((r) => r.json())
      .then((data) => { if (data.success) setMatches(data.fixtures || []); })
      .catch(() => {})
      .finally(() => { if (!silent) setLoading(false); });
  };

  useEffect(() => {
    fetchLive();
    const t = setInterval(() => fetchLive(true), 60000);
    return () => clearInterval(t);
  }, []);

  if (!loading && matches.length === 0) return null;

  return (
    <section>
      <SectionHeader
        title={`${t('home.liveNow')}${matches.length > 0 ? ` (${matches.length})` : ''}`}
        to="/sports"
      />
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '28px 0' }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ animation: 'spin 0.8s linear infinite' }}>
            <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
            <circle cx="12" cy="12" r="9" stroke="rgba(255,255,255,.1)" strokeWidth="2" />
            <path d="M12 3a9 9 0 018.66 6.5" stroke="#76e1ff" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </div>
      ) : (
        <div className="livenow-list">
          <div className="odds-col-header">
            <div className="odds-col-spacer" />
            <span>1</span>
            <span>X</span>
            <span>2</span>
          </div>
          {matches.map((m) => (
            <LiveCard key={m.fixture?.id} match={m} />
          ))}
        </div>
      )}
    </section>
  );
}
